import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	Store as SweetStateStore,
} from 'react-sweet-state';
import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

export type ParentContent = {
	id: string;
	title: string;
	contentType: string;
};

export type Content = {
	id: string;
	type: string;
	subType?: 'live' | null;
};

export type SubmitCallback = (props: { baseUrl: string; path: string }) => Promise<void>;

/**
 * Pre-fetched xFlow Jira data passed to SmartCreatePromptOnboarding.
 * Mirrors the `XFlowJiraData` type from
 * `@atlassian/conversation-assistant` without introducing a cross-package
 * dependency from the store.
 */
export type XFlowJiraData = {
	issue: {
		key: string | null;
		summary: string | null;
		webUrl: string | null;
	};
	project: {
		key: string | null;
		name: string | null;
		webUrl: string | null;
	};
	hasFullData: boolean;
};

/**
 * Raw onboarding context for the AIFC onboarding first screen.
 * Grouping these into a single object avoids prop-drilling five separate
 * fields through the modal stack.
 */
export type OnboardingData = {
	agentId?: string;
	contentType?: string;
	teamType?: string;
	/** Id of the template the user selected on the AIFC V2 content screen; drives the prompt heading. */
	templateId?: string;
	/**
	 * Opts into the template-driven onboarding variant (heading + land/cross-flow
	 * subtitle, and hides the template-suggestion pills) for the twcg-2046 AIFC
	 * Onboarding V2 experiment. Left unset on the control flow.
	 */
	isTemplateDrivenOnboarding?: boolean;
	isXFlowUser?: boolean;
	xFlowJiraData?: XFlowJiraData;
	/** Pre-formatted TWC prompt options to pass directly to the platform SmartCreationModal. */
	overridePromptOptions?: Array<{
		id: string;
		title: string;
		prompt: string;
		promptRegex: RegExp;
		imageType: 'page' | 'flowchart';
	}>;
	/** Entry point used to open the modal; consumed by platform-side onboarding UI to branch on TWC AIFC flow. */
	entryPoint?: string;
};

/**
 * Metadata for a Loom video attached to the smart-creation prompt. Mirrors
 * `LoomVideoMetadata` from `@confluence/smart-create` (a subset of the
 * @loomhq/record-sdk shape) without introducing a cross-package dependency
 * from the store. Persisted here so an SPA route change mid-recording — which
 * remounts the host modal — doesn't drop the attachment when the recorder's
 * orphaned "Add to prompt" callback fires after the remount.
 */
export type AttachedLoomVideo = {
	id: string;
	title: string;
	sharedUrl: string;
	embedUrl: string;
	thumbnailUrl?: string;
	thumbnailWidth?: number;
	thumbnailHeight?: number;
	duration?: number;
	description?: string;
	height: number;
	width: number;
};

export type OnCloseCallback = () => void;
export type OnOpenCallback = () => void;
export type OnSubmitCallback = () => void;
export type EditorInsertionNotAcknowledgedResult = {
	insertionKind: 'embed' | 'image';
	/**
	 * A timed-out invocation was either cancelled before delivery or remained in flight with an
	 * unknown insertion outcome.
	 */
	outcome: 'rejected' | 'timed-out-cancelled' | 'timed-out-in-flight';
};

export type EditorInsertionAcknowledgement = {
	/** Called after the editor confirms durable insertion. */
	onAcknowledged: () => void;
	/** Reports a failed or unknown attempt without changing recovery UI or lifecycle ownership. */
	onNotAcknowledged?: (result: EditorInsertionNotAcknowledgedResult) => void;
};

export type OutcomeType = 'liveDoc' | 'page' | 'whiteboard' | 'database' | 'slides' | 'agent';

type State = {
	showSmartCreationModal: boolean;
	/**
	 * True while the Loom recorder takes over the screen and the modal is
	 * hidden behind it. Lives in this remount-surviving store so an SPA route
	 * change mid-recording doesn't reset it and pop the modal back into view.
	 */
	isLoomRecorderOpen: boolean;
	/**
	 * The Loom video attached to the prompt, or null when none is attached.
	 * Lives in this remount-surviving store (rather than modal-local state) so
	 * the recorder's "Add to prompt" callback — captured by the SDK from the
	 * pre-navigation modal instance — still lands on the live modal after an
	 * SPA route change remounts it.
	 */
	attachedLoomVideo: AttachedLoomVideo | null;
	/**
	 * Sticky marker: a Loom-assisted prompt was submitted this session. Outlives
	 * `attachedLoomVideo` (cleared on submit) so the later create/finalize step
	 * can attribute the created content to Loom. In the store to survive remounts.
	 */
	submittedWithLoomAttachment: boolean;
	spaceKey: string;
	parentContent: ParentContent;
	content?: Content;
	stagingAreaActive: boolean;
	isFloating: boolean;
	shouldShowBackButton?: boolean;
	entryPoint: string;
	initialPrompt?: string;
	initialOutcomeType?: OutcomeType;
	/**
	 * When `true`, the `initialOutcomeType` pill is locked for the session
	 * (cross hidden, click no-op). Opt-in per entrypoint — e.g. set by the
	 * `cwr_blank_object_experiment` variant 3 entrypoints, left `false` by
	 * Slides.
	 */
	lockInitialOutcomeType: boolean;
	remixConversationId: string | null;
	/**
	 * Grouped onboarding context for the AIFC onboarding first screen.
	 * Undefined when the modal is not opened from the onboarding flow.
	 */
	onboardingData?: OnboardingData;
	/** Called after the onboarding prompt is submitted. No args — advances the step. */
	onOnboardingSubmit?: () => void;
	/**
	 * Total number of steps in the onboarding flow. Used to render the step
	 * indicator dots in the modal header dynamically.
	 */
	totalSteps?: number;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
	onOpenCallback?: OnOpenCallback;
	onSubmitCallback?: OnSubmitCallback;
	/** See `OpenModalParams.editorInsertionAcknowledgement`. */
	editorInsertionAcknowledgement?: EditorInsertionAcknowledgement;
};

const initialState: State = {
	showSmartCreationModal: false,
	isLoomRecorderOpen: false,
	attachedLoomVideo: null,
	submittedWithLoomAttachment: false,
	spaceKey: '',
	parentContent: {
		id: '',
		title: '',
		contentType: '',
	},
	content: undefined,
	stagingAreaActive: false,
	isFloating: false,
	shouldShowBackButton: undefined,
	entryPoint: 'confluenceUnknown',
	initialPrompt: undefined,
	initialOutcomeType: undefined,
	lockInitialOutcomeType: false,
	remixConversationId: null,
	onboardingData: undefined,
	onOnboardingSubmit: undefined,
	totalSteps: undefined,
	submitCallback: undefined,
	onCloseCallback: undefined,
	onOpenCallback: undefined,
	onSubmitCallback: undefined,
	editorInsertionAcknowledgement: undefined,
};

export type OpenModalParams = {
	entryPoint: string;
	spaceKey?: string;
	parentContent?: ParentContent;
	stagingAreaActive?: boolean;
	isFloating?: boolean;
	shouldShowBackButton?: boolean;
	content?: Content;
	initialPrompt?: string;
	initialOutcomeType?: OutcomeType;
	/** See `State.lockInitialOutcomeType`. */
	lockInitialOutcomeType?: boolean;
	remixConversationId?: string;
	/** Grouped onboarding context for the AIFC onboarding first screen. */
	onboardingData?: OnboardingData;
	/** Called after the onboarding prompt is submitted. No args — advances the step. */
	onOnboardingSubmit?: () => void;
	/** Total number of steps in the onboarding flow for the step indicator. */
	totalSteps?: number;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
	onOpenCallback?: OnOpenCallback;
	/**
	 * Observes every modal submit before either the custom `submitCallback` or the default submit
	 * handler runs. Unlike `submitCallback`, this does not replace submit behavior.
	 */
	onSubmitCallback?: OnSubmitCallback;
	/**
	 * When provided, finalize waits for the receiving editor to acknowledge durable insertion before
	 * reporting success. Independent of `onSubmitCallback`.
	 */
	editorInsertionAcknowledgement?: EditorInsertionAcknowledgement;
};

export const actions = {
	openModal:
		({
			entryPoint,
			spaceKey,
			parentContent,
			stagingAreaActive,
			isFloating,
			shouldShowBackButton,
			content,
			initialPrompt,
			initialOutcomeType,
			lockInitialOutcomeType,
			submitCallback,
			onCloseCallback,
			onOpenCallback,
			onSubmitCallback,
			editorInsertionAcknowledgement,
			remixConversationId: remixConversationIdFromOpen,
			onboardingData,
			onOnboardingSubmit,
			totalSteps,
		}: OpenModalParams) =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			const floating = isFloating ?? false;
			const remixConversationId =
				floating &&
				remixConversationIdFromOpen !== undefined &&
				remixConversationIdFromOpen !== null &&
				remixConversationIdFromOpen !== ''
					? remixConversationIdFromOpen
					: null;
			setState({
				showSmartCreationModal: true,
				// A fresh open is never behind the recorder; clearing here also
				// recovers if a prior recorder session left the flag stuck on.
				isLoomRecorderOpen: false,
				// A fresh open never carries a prior session's attachment.
				attachedLoomVideo: null,
				submittedWithLoomAttachment: false,
				entryPoint,
				spaceKey,
				parentContent: parentContent ?? {
					id: '',
					title: '',
					contentType: '',
				},
				content: content ?? undefined,
				stagingAreaActive: stagingAreaActive ?? false,
				isFloating: floating,
				shouldShowBackButton: shouldShowBackButton ?? undefined,
				initialPrompt,
				initialOutcomeType: initialOutcomeType ?? undefined,
				// Lock requires an initial outcome type — guard against misuse.
				lockInitialOutcomeType: Boolean(initialOutcomeType && lockInitialOutcomeType),
				remixConversationId,
				onboardingData: onboardingData ?? undefined,
				onOnboardingSubmit: onOnboardingSubmit ?? undefined,
				totalSteps: totalSteps ?? undefined,
				submitCallback: submitCallback ?? undefined,
				onCloseCallback: onCloseCallback ?? undefined,
				onOpenCallback: onOpenCallback ?? undefined,
				onSubmitCallback: onSubmitCallback ?? undefined,
				editorInsertionAcknowledgement: editorInsertionAcknowledgement ?? undefined,
			});
		},
	closeModal:
		() =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			setState({
				showSmartCreationModal: false,
				isLoomRecorderOpen: false,
				attachedLoomVideo: null,
				submittedWithLoomAttachment: false,
				content: undefined,
				stagingAreaActive: false,
				isFloating: false,
				shouldShowBackButton: undefined,
				entryPoint: 'confluenceUnknown',
				initialPrompt: undefined,
				initialOutcomeType: undefined,
				lockInitialOutcomeType: false,
				remixConversationId: null,
				onboardingData: undefined,
				onOnboardingSubmit: undefined,
				onCloseCallback: undefined,
				onOpenCallback: undefined,
				onSubmitCallback: undefined,
				editorInsertionAcknowledgement: undefined,
			});
		},
	setLoomRecorderOpen:
		(isLoomRecorderOpen: boolean) =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			setState({ isLoomRecorderOpen });
		},
	setAttachedLoomVideo:
		(attachedLoomVideo: AttachedLoomVideo | null) =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			setState({ attachedLoomVideo });
		},
	setSubmittedWithLoomAttachment:
		(submittedWithLoomAttachment: boolean) =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			setState({ submittedWithLoomAttachment });
		},
	// Atomically reads-and-clears the marker from live store state (not a
	// render-lagged hook value) so the create/finalize step sees the value set
	// by the submit that immediately preceded it, and never fires twice or leaks
	// onto a later create.
	consumeSubmittedWithLoomAttachment:
		() =>
		({
			getState,
			setState,
		}: {
			getState: () => State;
			setState: (state: Partial<State>) => void;
		}): boolean => {
			const submitted = getState().submittedWithLoomAttachment;
			if (submitted) {
				setState({ submittedWithLoomAttachment: false });
			}
			return submitted;
		},
	// Attaches a just-recorded Loom and forces the modal open. A recording can
	// finish after the modal was closed mid-record (reopened via global create,
	// then dismissed); the orphaned SDK "Add to prompt" callback fires here, so
	// reopening lets the attachment land on a visible modal instead of nowhere.
	attachLoomVideoAndOpenModal:
		(attachedLoomVideo: AttachedLoomVideo) =>
		({ setState }: { setState: (state: Partial<State>) => void }): void => {
			setState({ showSmartCreationModal: true, attachedLoomVideo });
		},
};

export const Store: SweetStateStore<
	State,
	{
		openModal: ({
			entryPoint,
			spaceKey,
			parentContent,
			stagingAreaActive,
			isFloating,
			shouldShowBackButton,
			content,
			initialPrompt,
			initialOutcomeType,
			lockInitialOutcomeType,
			submitCallback,
			onCloseCallback,
			onOpenCallback,
			remixConversationId,
			onboardingData,
			onOnboardingSubmit,
			totalSteps,
		}: OpenModalParams) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		closeModal: () => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		setLoomRecorderOpen: (
			isLoomRecorderOpen: boolean,
		) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		setAttachedLoomVideo: (
			attachedLoomVideo: AttachedLoomVideo | null,
		) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		setSubmittedWithLoomAttachment: (
			submittedWithLoomAttachment: boolean,
		) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		// Atomically reads-and-clears the marker from live store state (not a
		// render-lagged hook value) so the create/finalize step sees the value set
		// by the submit that immediately preceded it, and never fires twice or leaks
		// onto a later create.
		consumeSubmittedWithLoomAttachment: () => ({
			getState,
			setState,
		}: {
			getState: () => State;
			setState: (state: Partial<State>) => void;
		}) => boolean;
		// Attaches a just-recorded Loom and forces the modal open. A recording can
		// finish after the modal was closed mid-record (reopened via global create,
		// then dismissed); the orphaned SDK "Add to prompt" callback fires here, so
		// reopening lets the attachment land on a visible modal instead of nowhere.
		attachLoomVideoAndOpenModal: (
			attachedLoomVideo: AttachedLoomVideo,
		) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'smartCreationModalStore',
});

export const useSmartCreationModalStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			openModal: ({
				entryPoint,
				spaceKey,
				parentContent,
				stagingAreaActive,
				isFloating,
				shouldShowBackButton,
				content,
				initialPrompt,
				initialOutcomeType,
				lockInitialOutcomeType,
				submitCallback,
				onCloseCallback,
				onOpenCallback,
				remixConversationId,
				onboardingData,
				onOnboardingSubmit,
				totalSteps,
			}: OpenModalParams) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			closeModal: () => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setLoomRecorderOpen: (
				isLoomRecorderOpen: boolean,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setAttachedLoomVideo: (
				attachedLoomVideo: AttachedLoomVideo | null,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setSubmittedWithLoomAttachment: (
				submittedWithLoomAttachment: boolean,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			// Atomically reads-and-clears the marker from live store state (not a
			// render-lagged hook value) so the create/finalize step sees the value set
			// by the submit that immediately preceded it, and never fires twice or leaks
			// onto a later create.
			consumeSubmittedWithLoomAttachment: () => ({
				getState,
				setState,
			}: {
				getState: () => State;
				setState: (state: Partial<State>) => void;
			}) => boolean;
			// Attaches a just-recorded Loom and forces the modal open. A recording can
			// finish after the modal was closed mid-record (reopened via global create,
			// then dismissed); the orphaned SDK "Add to prompt" callback fires here, so
			// reopening lets the attachment land on a visible modal instead of nowhere.
			attachLoomVideoAndOpenModal: (
				attachedLoomVideo: AttachedLoomVideo,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		}
	>,
	void
> = createHook(Store);

export const useSmartCreationModalActions: HookActionsFunction<
	BoundActions<
		State,
		{
			openModal: ({
				entryPoint,
				spaceKey,
				parentContent,
				stagingAreaActive,
				isFloating,
				shouldShowBackButton,
				content,
				initialPrompt,
				initialOutcomeType,
				lockInitialOutcomeType,
				submitCallback,
				onCloseCallback,
				onOpenCallback,
				remixConversationId,
				onboardingData,
				onOnboardingSubmit,
				totalSteps,
			}: OpenModalParams) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			closeModal: () => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setLoomRecorderOpen: (
				isLoomRecorderOpen: boolean,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setAttachedLoomVideo: (
				attachedLoomVideo: AttachedLoomVideo | null,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			setSubmittedWithLoomAttachment: (
				submittedWithLoomAttachment: boolean,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
			// Atomically reads-and-clears the marker from live store state (not a
			// render-lagged hook value) so the create/finalize step sees the value set
			// by the submit that immediately preceded it, and never fires twice or leaks
			// onto a later create.
			consumeSubmittedWithLoomAttachment: () => ({
				getState,
				setState,
			}: {
				getState: () => State;
				setState: (state: Partial<State>) => void;
			}) => boolean;
			// Attaches a just-recorded Loom and forces the modal open. A recording can
			// finish after the modal was closed mid-record (reopened via global create,
			// then dismissed); the orphaned SDK "Add to prompt" callback fires here, so
			// reopening lets the attachment land on a visible modal instead of nowhere.
			attachLoomVideoAndOpenModal: (
				attachedLoomVideo: AttachedLoomVideo,
			) => ({ setState }: { setState: (state: Partial<State>) => void }) => void;
		}
	>
> = createActionsHook(Store);
export const useSmartCreationModalState: HookStateFunction<State, void> = createStateHook(Store);
